import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { calculateDiscount } from '@/utils/payment/discount';
import { replaceLocationBasedTerms } from '@/utils/replaceLocationBasedTerms';
import { formattedDiscountPrice, formattedSkuPrice, skuPrice } from '@/utils/SkuDetails';
import { Container, Typography } from '@prenuvo/halo-web';
import { formatInTimeZone } from 'date-fns-tz';
import { useShallow } from 'zustand/react/shallow';

export const BookingSummary = () => {
  const {
    discount,
    discountType,
    referralCredit,
    marketingReferral,
    location,
    selectedSku,
    locationId,
    utcStart,
    setFinalPrice,
  } = usePrenuvoStore(
    useShallow((state) => ({
      discount: state.payment.discount,
      referralCredit: state.payment.referralCredit,
      discountType: state.payment.discountType,
      location: state.scan.location,
      marketingReferral: state.payment.marketingReferral,
      selectedSku: state.scan.sku,
      locationId: state.scan.locationId,
      utcStart: state.scan.slot?.utcStart,
      setFinalPrice: state.payment.methods.add,
    })),
  );

  const { finalPrice, totalDiscount, usedReferralCredit } = calculateDiscount({
    sku: selectedSku,
    discount,
    discountType,
    marketingReferral,
    referralCredit,
    locationId,
  });

  setFinalPrice({
    finalPrice: (selectedSku && skuPrice(selectedSku, totalDiscount)) || 0,
  });

  return (
    <Container className="mb-10 flex flex-col divide-y divide-geyser-200" variant="inset">
      <div className="flex flex-col gap-4 pb-4">
        <Typography variant="paragraph-md-bold" as="p">
          {replaceLocationBasedTerms('Booking Summary', locationId)}
        </Typography>
        <div className="flex flex-col">
          <Typography
            variant="label-md"
            as="p"
            className="text-neutral-500"
            data-testid="scan-type"
          >
            Scan Type
          </Typography>
          <Typography variant="paragraph-md" as="p" data-testid="sku-shortname">
            {selectedSku?.shortName}
          </Typography>
        </div>
        <div className="flex flex-col">
          <Typography
            variant="label-md"
            as="p"
            className="text-neutral-500"
            data-testid="location-label"
          >
            Location
          </Typography>
          <Typography variant="paragraph-md" as="p" data-testid="location">
            {location
              ? `${location?.address?.city}, ${location?.address?.state}, ${location?.address?.country?.code}`
              : ''}
          </Typography>
        </div>
        <div className="flex flex-col">
          <Typography
            variant="label-md"
            as="p"
            className="text-neutral-500"
            data-testid="scan-date-label"
          >
            Scan Date
          </Typography>
          <Typography variant="paragraph-md" as="p" data-testid="scan-date">
            {utcStart && location?.timezone
              ? `${formatInTimeZone(new Date(utcStart * 1000), location.timezone, 'EEE, MMM dd, hh:mm aa')} (${location?.displayTimezone})`
              : null}
          </Typography>
        </div>
      </div>
      <div className="grid gap-2 py-4" data-testid="payment-summary">
        <div className="pb-2">
          <Typography variant="paragraph-sm-bold" as="p">
            Payment Summary
          </Typography>
        </div>
        <div className="flex justify-between" data-testid="payment-item">
          <Typography variant="label-sm" as="label" className="max-w-[60%] text-neutral-500">
            {selectedSku?.shortName}
          </Typography>
          <Typography variant="label-sm" as="label" className="text-neutral-500">
            {selectedSku && formattedSkuPrice(selectedSku)}
          </Typography>
        </div>
        <div className="flex justify-between">
          <Typography variant="label-sm" as="label" className="text-neutral-500">
            Subtotal
          </Typography>
          <Typography variant="label-sm" as="label" className="text-neutral-500">
            {selectedSku && formattedSkuPrice(selectedSku)}
          </Typography>
        </div>
        {location && totalDiscount ? (
          <div className="flex justify-between" data-testid="discount-applied">
            {usedReferralCredit ? (
              <Typography variant="label-sm" as="label" className="text-greenSuccess">
                Referral/Discount Credit
              </Typography>
            ) : (
              <Typography variant="label-sm" as="label" className="text-neutral-500">
                Discount Applied
              </Typography>
            )}
            <Typography variant="label-sm" as="label" className="text-neutral-500">
              {selectedSku
                ? `-${selectedSku?.price?.currency} ${usedReferralCredit && finalPrice === 0 ? Number(selectedSku?.price?.total).toFixed(2) : totalDiscount}`
                : null}
            </Typography>
          </div>
        ) : null}
      </div>
      <div className="pt-4" data-testid="total-summary">
        <div className="flex justify-between">
          <Typography variant="paragraph-lg-bold" as="p">
            Total
          </Typography>
          <Typography variant="paragraph-lg-bold" as="p">
            {selectedSku && formattedSkuPrice(selectedSku, totalDiscount)}
          </Typography>
        </div>
      </div>
    </Container>
  );
};
